import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useLanguage } from '@/contexts';
import { UsageWithPercentage, FeatureKey } from '@/types/subscription';

/**
 * Threshold levels for usage alerts
 */
export const USAGE_THRESHOLDS = {
  WARNING: 75,
  URGENT: 90,
  LIMIT: 100,
} as const;

/**
 * Auto-dismiss duration in milliseconds
 */
export const TOAST_DURATION = {
  WARNING: 5000,
  URGENT: 5000,
  LIMIT: Infinity,
} as const;

export interface UseUsageAlertsOptions {
  /**
   * Whether to enable usage alerts
   * @default true
   */
  enabled?: boolean;
  
  /**
   * Callback when a threshold is crossed
   */
  onThresholdCrossed?: (threshold: number, usage: UsageWithPercentage) => void;
}

/**
 * Hook to show toast notifications when usage thresholds are crossed
 * 
 * Monitors usage data and displays appropriate warnings:
 * - 75%: Warning toast (auto-dismiss after 5s)
 * - 90%: Urgent warning toast (auto-dismiss after 5s)
 * - 100%: Error toast (no auto-dismiss)
 * 
 * Features:
 * - Prevents duplicate notifications using notifiedRef
 * - Higher thresholds override lower ones
 * - Supports i18n translations
 * - Only notifies on threshold crossing, not while staying at threshold
 * 
 * @param usage - Array of usage data with percentages
 * @param options - Configuration options
 */
export function useUsageAlerts(
  usage: UsageWithPercentage[],
  options: UseUsageAlertsOptions = {}
) {
  const { enabled = true, onThresholdCrossed } = options;
  const { t } = useLanguage();
  
  // Track which thresholds have been notified per feature
  // Key format: "featureKey:threshold" (e.g., "private_prompts:75")
  const notifiedRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    if (!enabled || !usage || usage.length === 0) {
      return;
    }

    // Process each feature's usage
    usage.forEach((item) => {
      // Skip unlimited features
      if (item.isUnlimited || item.percentage_used === null) {
        return;
      }

      const percentage = item.percentage_used;
      const featureKey = item.feature_key;
      
      // Determine which threshold is crossed
      let currentThreshold: number | null = null;
      
      if (percentage >= USAGE_THRESHOLDS.LIMIT) {
        currentThreshold = USAGE_THRESHOLDS.LIMIT;
      } else if (percentage >= USAGE_THRESHOLDS.URGENT) {
        currentThreshold = USAGE_THRESHOLDS.URGENT;
      } else if (percentage >= USAGE_THRESHOLDS.WARNING) {
        currentThreshold = USAGE_THRESHOLDS.WARNING;
      }

      // Get the last notified threshold for this feature
      const lastNotifiedThreshold = notifiedRef.current.get(featureKey) || 0;

      // Only show notification if:
      // 1. We've crossed a new threshold
      // 2. The new threshold is higher than the last one we notified about
      if (currentThreshold !== null && currentThreshold > lastNotifiedThreshold) {
        showThresholdToast(currentThreshold, item, t, featureKey);
        notifiedRef.current.set(featureKey, currentThreshold);
        
        // Call optional callback
        if (onThresholdCrossed) {
          onThresholdCrossed(currentThreshold, item);
        }
      }
    });
  }, [usage, enabled, onThresholdCrossed, t]);

  /**
   * Reset notification tracking for a specific feature or all features
   */
  const resetNotifications = (featureKey?: FeatureKey) => {
    if (featureKey) {
      notifiedRef.current.delete(featureKey);
    } else {
      notifiedRef.current.clear();
    }
  };

  return { resetNotifications };
}

/**
 * Display the appropriate toast notification for a threshold
 */
function showThresholdToast(
  threshold: number,
  usage: UsageWithPercentage,
  t: (key: string, options?: Record<string, string | number | null>) => string,
  featureKey: FeatureKey
) {
  const featureName = getFeatureName(featureKey, t);
  const percentage = usage.percentage_used || 0;
  const current = usage.current_usage;
  const limit = usage.limit_value;

  if (threshold === USAGE_THRESHOLDS.LIMIT) {
    // 100% - Error toast, no auto-dismiss
    const message = t('usageAlerts.limitReached', {
      feature: featureName,
      current,
      limit,
    });
    
    toast.error(message, {
      autoClose: false,
      closeButton: true,
    });
  } else if (threshold === USAGE_THRESHOLDS.URGENT) {
    // 90% - Urgent warning toast (orange/red)
    const message = t('usageAlerts.almostAtLimit', {
      current,
      limit,
      feature: featureName,
    });
    
    toast.warning(message, {
      autoClose: TOAST_DURATION.URGENT,
      style: {
        background: '#fed7aa', // orange-200
        color: '#9a3412', // orange-800
      },
    });
  } else if (threshold === USAGE_THRESHOLDS.WARNING) {
    // 75% - Warning toast
    const message = t('usageAlerts.highUsage', {
      percentage,
      feature: featureName,
    });
    
    toast.warning(message, {
      autoClose: TOAST_DURATION.WARNING,
    });
  }
}

/**
 * Get localized feature name
 */
function getFeatureName(featureKey: FeatureKey, t: (key: string) => string): string {
  const translations: Record<FeatureKey, string> = {
    'private_prompts': t('privatePrompts') || 'Private Prompts',
    'collections': t('collections') || 'Collections',
    'api_calls': t('apiCalls') || 'API Calls',
    'public_prompts': t('publicPrompts') || 'Public Prompts',
  };
  return translations[featureKey];
}

